// Utility Solutions scroll carousel (related work). Native scroll-snap track;
// prev/next scroll by one tile and disable at either end (both stay visible,
// as in Figma, even when every tile fits).
// Inert when `[data-us-carousel]` is absent.
// eslint-disable-next-line no-unused-vars -- global, called from us-main.js
function usCarouselInit() {
  document.querySelectorAll("[data-us-carousel]").forEach(function (root) {
    var track = root.querySelector("[data-us-carousel-track]");
    var prev = root.querySelector("[data-us-carousel-prev]");
    var next = root.querySelector("[data-us-carousel-next]");
    if (!track || !prev || !next) {
      return;
    }

    function step() {
      var tile = track.firstElementChild;
      var gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      return tile
        ? tile.getBoundingClientRect().width + gap
        : track.clientWidth;
    }

    function update() {
      var max = track.scrollWidth - track.clientWidth;
      prev.disabled = track.scrollLeft <= 1;
      next.disabled = track.scrollLeft >= max - 1;
    }

    prev.addEventListener("click", function () {
      track.scrollBy({ left: -step() });
    });
    next.addEventListener("click", function () {
      track.scrollBy({ left: step() });
    });

    track.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    update();
  });
}

// Utility Solutions case body (`.cc.us-case-body`). On desktop the summary is
// sticky (CSS). When it is taller than the space below the header (e.g. a
// 1366×768 laptop), a top offset would hide its lower part, so it pins by its
// bottom edge instead, 32px above the viewport bottom. Inert elsewhere.
// eslint-disable-next-line no-unused-vars -- global, called from us-main.js
function usCaseBodyInit() {
  var summary = document.querySelector(".cc.us-case-body .us-case-body__summary");
  if (!summary) {
    return;
  }

  var gap = 32;

  function fit() {
    summary.style.top = "";
    var style = window.getComputedStyle(summary);
    if (style.position !== "sticky") {
      return;
    }
    var top = parseFloat(style.top) || 0;
    var room = window.innerHeight - top - gap;
    if (summary.offsetHeight > room) {
      summary.style.top = window.innerHeight - summary.offsetHeight - gap + "px";
    }
  }

  fit();
  window.addEventListener("resize", fit);
  // Web fonts and late images can change the summary's height after load.
  if ("ResizeObserver" in window) {
    new ResizeObserver(fit).observe(summary);
  }
}

// Utility Solutions header (`.cc.us-header`). The checkbox drawer works without
// JS; this adds aria-expanded/label sync, Escape to close, a focus trap while
// the drawer is open, close-on-anchor-click, body scroll lock, the sticky bar
// and in-page section highlighting. Inert on pages without the header.
// eslint-disable-next-line no-unused-vars -- global, called from us-main.js
function usHeaderInit() {
  var header = document.getElementById("us-masthead");
  var toggle = document.getElementById("usHamburger");
  if (!header || !toggle) {
    return;
  }

  var desktop = window.matchMedia("(min-width: 1200px)");
  var links = Array.prototype.slice.call(
    header.querySelectorAll(".us-header__link"),
  );
  var logo = header.querySelector(".us-header__logo");

  function sync() {
    var open = toggle.checked;
    toggle.setAttribute("aria-expanded", open ? "true" : "false");
    toggle.setAttribute(
      "aria-label",
      toggle.getAttribute(open ? "data-menu-close" : "data-menu-open"),
    );
    document.body.classList.toggle("us-menu-open", open);
  }

  function close() {
    if (!toggle.checked) {
      return;
    }
    toggle.checked = false;
    sync();
  }

  toggle.addEventListener("change", sync);

  // Enter opens/closes like Space (checkbox only responds to Space natively).
  toggle.addEventListener("keydown", function (e) {
    if (e.key === "Enter") {
      e.preventDefault();
      toggle.checked = !toggle.checked;
      sync();
    }
  });

  document.addEventListener("keydown", function (e) {
    if (!toggle.checked || desktop.matches) {
      return;
    }

    if (e.key === "Escape") {
      close();
      toggle.focus();
      return;
    }

    // Focus trap: logo → toggle → links, wrapping at both ends.
    if (e.key === "Tab") {
      var focusables = [logo, toggle].concat(links).filter(Boolean);
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      var active = document.activeElement;
      var inside = focusables.indexOf(active) !== -1;

      if (e.shiftKey && (active === first || !inside)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (active === last || !inside)) {
        e.preventDefault();
        first.focus();
      }
    }
  });

  // In-page anchors: close the drawer so the target section is visible.
  links.forEach(function (link) {
    link.addEventListener("click", close);
  });

  // Back to desktop width with the drawer open → reset state.
  desktop.addEventListener("change", close);

  function onScroll() {
    header.classList.toggle("us-header--sticky", window.scrollY > 8);
  }
  onScroll();
  window.addEventListener("scroll", onScroll, { passive: true });

  // Highlight the link for the section currently in view (same-page anchors only).
  if ("IntersectionObserver" in window) {
    var byId = {};
    links.forEach(function (link) {
      var href = link.getAttribute("href") || "";
      if (href.charAt(0) === "#" && href.length > 1) {
        var section = document.getElementById(href.slice(1));
        if (section) {
          byId[section.id] = link;
        }
      }
    });

    var sections = Object.keys(byId).map(function (id) {
      return document.getElementById(id);
    });

    if (sections.length) {
      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              links.forEach(function (link) {
                link.classList.remove("is-active");
              });
              byId[entry.target.id].classList.add("is-active");
            }
          });
        },
        { rootMargin: "-45% 0px -50% 0px" },
      );
      sections.forEach(function (section) {
        observer.observe(section);
      });
    }
  }

  sync();
}

// Utility Solutions entry point — bundled last into utility-solutions.js and
// loaded only on the Utility Solutions templates. Each init exits early when
// its markup is absent.
/* global usHeaderInit, usTestimonialInit, usCarouselInit, usTeamInit, usCaseBodyInit */
document.addEventListener("DOMContentLoaded", function () {
  usHeaderInit();
  usTestimonialInit();
  usCarouselInit();
  usTeamInit();
  usCaseBodyInit();
});

// Leadership team cards open the site's existing team member popup
// (team-member-modal, run by the site-wide main.js). The card only fires the
// same `cc:team-member-open` event the People grid uses; the popup loads the
// bio itself. Without jQuery or the popup on the page, the card does nothing.
// eslint-disable-next-line no-unused-vars -- global, called from us-main.js
function usTeamInit() {
  var grid = document.querySelector(".us-team__grid");
  if (!grid || !window.jQuery || !document.querySelector(".js-team-member-modal")) {
    return;
  }

  grid.addEventListener("click", function (event) {
    var card = event.target.closest(".js-us-team-card");
    if (!card) {
      return;
    }
    event.preventDefault();
    var $ = window.jQuery;
    $(document).trigger("cc:team-member-open", [
      {
        id: card.getAttribute("data-team-member-id"),
        $trigger: $(card),
      },
    ]);
  });
}

// Utility Solutions testimonial slider. Cross-fades slides that share one grid
// cell; dots, prev/next and ←/→ keys move between them. Inactive slides are
// hidden from assistive tech; the slides wrapper is an aria-live region.
// No autoplay. Inert when `[data-us-testimonial]` is absent.
// eslint-disable-next-line no-unused-vars -- global, called from us-main.js
function usTestimonialInit() {
  document.querySelectorAll("[data-us-testimonial]").forEach(function (card) {
    var slides = Array.prototype.slice.call(
      card.querySelectorAll(".us-testimonial__slide"),
    );
    var dots = Array.prototype.slice.call(
      card.querySelectorAll("[data-us-slide-to]"),
    );
    var prev = card.querySelector("[data-us-prev]");
    var next = card.querySelector("[data-us-next]");
    var current = 0;

    if (slides.length < 2) {
      return;
    }

    function go(index) {
      current = (index + slides.length) % slides.length;
      slides.forEach(function (slide, i) {
        var active = i === current;
        slide.classList.toggle("is-active", active);
        slide.setAttribute("aria-hidden", active ? "false" : "true");
      });
      dots.forEach(function (dot, i) {
        var active = i === current;
        dot.classList.toggle("is-active", active);
        dot.setAttribute("aria-current", active ? "true" : "false");
      });
    }

    dots.forEach(function (dot) {
      dot.addEventListener("click", function () {
        go(parseInt(dot.getAttribute("data-us-slide-to"), 10));
      });
    });

    if (prev) {
      prev.addEventListener("click", function () {
        go(current - 1);
      });
    }

    if (next) {
      next.addEventListener("click", function () {
        go(current + 1);
      });
    }

    card.addEventListener("keydown", function (e) {
      if (e.key === "ArrowLeft") {
        go(current - 1);
      } else if (e.key === "ArrowRight") {
        go(current + 1);
      }
    });

    go(0);
  });
}
